import {
  useQuery, useMutation, useQueryClient 
} from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getDeliveryZones,
  getDeliveryZone,
  updateDeliveryZone,
  toggleZoneStatus,
} from '@/api/zones-client';
import type { DeliveryZone } from '@/types/zones';

export function useDeliveryZones() {
  return useQuery({
    queryKey: ['delivery-zones'],
    queryFn: getDeliveryZones,
  });
}

export function useDeliveryZone(id: string) {
  return useQuery({
    queryKey: ['delivery-zone', id],
    queryFn: () => getDeliveryZone(id),
    enabled: Boolean(id),
  });
}

export function useUpdateDeliveryZone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id, updates 
    }: {
      id: string;
      updates: Partial<DeliveryZone> 
    }) =>
      updateDeliveryZone(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-zones'] });
      toast.success('Delivery zone updated successfully');
    },
    onError: () => {
      toast.error('Failed to update delivery zone');
    },
  });
}

export function useToggleZoneStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toggleZoneStatus,
    onSuccess: (zone) => {
      queryClient.invalidateQueries({ queryKey: ['delivery-zones'] });
      toast.success(`Zone ${zone.isActive ? 'activated' : 'deactivated'} successfully`);
    },
    onError: () => {
      toast.error('Failed to update zone status');
    },
  });
}
